import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';

// Error Boundary
import { ErrorBoundary } from './components/ErrorBoundary';

// Theme Context
import { ThemeProvider } from './contexts/ThemeContext';

// Auth Context
import { AuthProvider } from './contexts/AuthContext';

// Data Context
import { DataProvider } from './contexts/DataContext';

// Layout Components
import Navigation from './components/layout/Navigation';
import ThemedBackground from './components/layout/ThemedBackground';

// Lazy-loaded Section Components
const Hero = lazy(() => import('./components/sections/Hero'));
const About = lazy(() => import('./components/sections/About'));
const Experience = lazy(() => import('./components/sections/Experience'));
const Projects = lazy(() => import('./components/sections/Projects'));
const Skills = lazy(() => import('./components/sections/Skills'));
const Education = lazy(() => import('./components/sections/Education'));
const Contact = lazy(() => import('./components/sections/Contact'));
const Blog = lazy(() => import('./components/sections/Blog'));
const BlogPost = lazy(() => import('./components/sections/BlogPost'));
const AllBlogPosts = lazy(() => import('./components/sections/AllBlogPosts'));

// Admin Components
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const BlogManagement = lazy(() => import('./components/admin/BlogManagement'));
const SiteDataManagement = lazy(() => import('./components/admin/SiteDataManagement'));
const ProtectedRoute = lazy(() => import('./components/admin/ProtectedRoute'));

// Loading component
const SectionLoading = () => (
  <div className="flex items-center justify-center py-20">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="min-h-screen transition-colors duration-300">
      <ThemedBackground />
      {!isAdminRoute && <Navigation />}
      
      <main>
        <Suspense fallback={<SectionLoading />}>
          <Routes>
            <Route path="/" element={
              <>
                <Hero />
                <About />
                <Experience />
                <Projects />
                <Skills />
                <Education />
                <Blog />
                <Contact />
              </>
            } />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog" element={<AllBlogPosts />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }>
              <Route path="blog" element={<BlogManagement />} />
              <Route path="site" element={<SiteDataManagement />} />
            </Route>
          </Routes>
        </Suspense>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <DataProvider>
          <ThemeProvider>
            <Router>
              <AppContent />
            </Router>
          </ThemeProvider>
        </DataProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
